import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Download, Minimize2, Sparkles } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import {
  PageHeader,
  SectionCard,
  StatTile,
  Tabs,
} from "@/features/power_projects/components/ui";
import { fmtDateTime } from "@/features/power_projects/utils";
import { TenderDetailPage } from "@/features/tenders/pages/TenderDetailPage";
import {
  useCancelTask,
  useCreateZEMapAnnotation,
  useCreateZEVariant,
  useCopyZEVariant,
  useDeleteZEMapAnnotation,
  useDeleteZEVariant,
  useInvalidateAfterRecompute,
  usePatchZEVariant,
  useQuantitySummary,
  useReanalyzeZETender,
  useRecomputeParcels,
  useRecomputeRoadNeighbors,
  useSaveDrawing,
  useSaveZETenderParcelDraft,
  useTaskPolling,
  useUpdateZEMapAnnotation,
  useVariantDrawing,
  useZECapabilities,
  useZEElementTypes,
  useZEMapAnnotations,
  useZETender,
  useZETenderAiAnalysis,
  useZETenderParcelDrafts,
  useZETenderParcels,
  useZERoadNeighbors,
  useVariantLayerPresets,
  useZEVariants,
} from "../hooks";
import {
  ZEDrawingMap,
  type ZEDrawingMapHandle,
} from "../components/ZEDrawingMap";
import { ZEElementPalette } from "../components/ZEElementPalette";
import { ZEParcelsPanel } from "../components/ZEParcelsPanel";
import { ZERepeatMatchBanner } from "../components/ZERepeatMatchBanner";
import { ZEQuantitySummaryPanel } from "../components/ZEQuantitySummaryPanel";
import { ZETakeoffDefaultsPanel } from "../components/ZETakeoffDefaultsPanel";
import { ZEVariantSwitcher } from "../components/ZEVariantSwitcher";
import type {
  QuantitySummary,
  ZEDrawingFeatureCollection,
  ZEElementType,
  ZEMapAnnotation,
  ZETenderAiAnalysis,
} from "../types";
import { findOwnershipOption, localQuantitySummary } from "../utils";
import * as zeApi from "../api";
import { useStartTakeoffJob, useTenderTakeoffJob } from "@/features/estimating/hooks";
import { EstimateResultPage } from "@/features/estimating/pages/EstimateResultPage";
import { downloadBlob } from "@/utils/download";

const EMPTY_SUMMARY: QuantitySummary = { lines: [], objects: [], params: {} };

/** Pull a human-readable message out of an API error ({ data, errors, meta }). */
function extractError(err: unknown): string {
  const e = err as {
    response?: {
      data?: { errors?: Record<string, unknown> | string[] | string };
    };
    message?: string;
  };
  const errs = e?.response?.data?.errors;
  if (typeof errs === "string" && errs) return errs;
  if (Array.isArray(errs) && errs.length) return errs.join(" ");
  if (errs && typeof errs === "object") {
    const obj = errs as Record<string, unknown>;
    if (typeof obj.detail === "string") return obj.detail;
    const flat = Object.values(obj).flat().filter(Boolean);
    if (flat.length) return flat.join(" ");
  }
  return e?.message || "Coś jest popsute — spróbuj ponownie.";
}

/**
 * Everything the drawing workspace remembers PER BROWSER, not per user or per tender.
 *
 * This is why the same tender can open on one machine and fail on another: which GUGiK
 * overlays are on, and whether length labels are drawn, decide which layers get built at
 * all. A crash tied to one of those choices follows the browser, not the record — so the
 * recovery panel offers to wipe exactly these keys.
 */
const ZE_MAP_VIEW_KEYS = [
  "gugik.layers.zeTenders.drawing",
  "zeTenders.drawing.showLineLengths",
  "ze_ann_panel_collapsed",
  "ze_fs_col_widths",
];

const FS_WIDTHS_KEY = "ze_fs_col_widths";
const FS_WIDTHS_DEFAULT = { left: 380, right: 240 };
function loadFsWidths() {
  try {
    const v = localStorage.getItem(FS_WIDTHS_KEY);
    if (v) {
      const p = JSON.parse(v);
      if (p?.left && p?.right) return p as typeof FS_WIDTHS_DEFAULT;
    }
  } catch {}
  return FS_WIDTHS_DEFAULT;
}

const SCOPE_LABEL: Record<string, string> = {
  projekt: "Projekt",
  wykonawstwo: "Wykonawstwo",
  projekt_i_wykonawstwo: "Projekt i wykonawstwo",
};
const PROJECT_TYPE_LABEL: Record<string, string> = {
  short_connection: "Krótki projekt — przyłącze",
  single_parcel: "Jedna działka",
  line_or_multi_parcel: "Linia / wiele działek",
  unknown: "Nieznany",
};
const CONFIDENCE_LABEL: Record<string, string> = {
  confirmed: "Potwierdzona",
  low_confidence: "Niska pewność",
  needs_vision: "Wymaga przeglądu",
  failed: "Nie zlokalizowano",
};
const CONFIDENCE_COLOR: Record<string, string> = {
  confirmed: "text-emerald-600",
  low_confidence: "text-amber-500",
  failed: "text-red-500",
};

function AiRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex gap-3 py-1.5 border-b border-[var(--color-border-muted)] last:border-0">
      <span className="w-40 shrink-0 text-xs text-[var(--color-text-tertiary)]">
        {label}
      </span>
      <span className="text-xs text-[var(--color-text-primary)]">{value}</span>
    </div>
  );
}

function computeWarnings(
  signals:
    | { rkw_required?: string[]; contract_scope?: string | null }
    | undefined,
  oddzial: string,
  caps:
    | { proj_rkw: string[]; bud_rkw: string[]; porozumienia_oddzialy: string[] }
    | undefined,
): string[] {
  if (!caps || !signals) return [];
  const scope = signals.contract_scope ?? "";
  const rkw = signals.rkw_required ?? [];
  const isProj = scope === "projekt" || scope === "projekt_i_wykonawstwo";
  const isBud = scope === "wykonawstwo" || scope === "projekt_i_wykonawstwo";
  const warnings: string[] = [];
  if (isProj) {
    const projSet = new Set(caps.proj_rkw);
    rkw.forEach((r) => {
      if (!projSet.has(r)) warnings.push(`Brak uprawnień projektowych: ${r}`);
    });
  }
  if (isBud) {
    const budSet = new Set(caps.bud_rkw);
    rkw.forEach((r) => {
      if (!budSet.has(r)) warnings.push(`Brak uprawnień wykonawczych: ${r}`);
    });
    if (!caps.porozumienia_oddzialy.length) {
      warnings.push(
        "Brak porozumień z oddziałami ENERGA — roboty budowlane niedostępne",
      );
    } else if (oddzial) {
      const norm = oddzial.toLowerCase();
      if (
        !caps.porozumienia_oddzialy.some(
          (o) =>
            norm.includes(o.toLowerCase()) || o.toLowerCase().includes(norm),
        )
      )
        warnings.push(`Brak porozumienia z oddziałem: ${oddzial}`);
    }
  }
  return warnings;
}

function RkwBadges({
  rkw,
  scope,
  warnings,
}: {
  rkw: string[];
  scope: string | null | undefined;
  warnings: string[];
}) {
  const isProj = scope === "projekt" || scope === "projekt_i_wykonawstwo";
  const isBud = scope === "wykonawstwo" || scope === "projekt_i_wykonawstwo";
  const missingProj = new Set(
    warnings
      .filter((w) => w.includes("projektowych"))
      .map((w) => w.replace("Brak uprawnień projektowych: ", "")),
  );
  const missingBud = new Set(
    warnings
      .filter((w) => w.includes("wykonawczych"))
      .map((w) => w.replace("Brak uprawnień wykonawczych: ", "")),
  );
  const allOk = missingProj.size === 0 && missingBud.size === 0;
  return (
    <div className="space-y-1.5">
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded ${
          allOk
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400"
            : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
        }`}
      >
        {allOk
          ? "✓ Uprawnienia OK"
          : `⚠ Brak ${missingProj.size + missingBud.size} uprawnień`}
      </span>
      <div className="flex flex-wrap gap-1">
        {rkw.map((r) => {
          const missing =
            (isProj && missingProj.has(r)) || (isBud && missingBud.has(r));
          return (
            <span
              key={r}
              className={`text-[10px] px-1.5 py-0.5 rounded font-mono flex items-center gap-0.5 ${
                missing
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
                  : "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
              }`}
            >
              {missing ? "✗" : "✓"} {r}
            </span>
          );
        })}
      </div>
      {/* Per-type explanation for missing items */}
      {!allOk && (
        <div className="space-y-0.5 pt-0.5">
          {[...missingProj].map((r) => (
            <p
              key={`p-${r}`}
              className="text-[10px] text-amber-700 dark:text-amber-400"
            >
              ✗ projekt: brak uprawnienia do projektowania — <em>{r}</em>
            </p>
          ))}
          {[...missingBud].map((r) => (
            <p
              key={`b-${r}`}
              className="text-[10px] text-amber-700 dark:text-amber-400"
            >
              ✗ wykonawstwo: brak uprawnienia do budowy — <em>{r}</em>
            </p>
          ))}
          {warnings
            .filter((w) => w.includes("porozumie"))
            .map((w, i) => (
              <p
                key={`poro-${i}`}
                className="text-[10px] text-amber-700 dark:text-amber-400"
              >
                ⚠ {w}
              </p>
            ))}
        </div>
      )}
    </div>
  );
}

function AiAnalysisTab({
  analysis,
  tenderId,
}: {
  analysis: ZETenderAiAnalysis;
  tenderId: string;
}) {
  const sig = analysis.signals;
  const parcels = analysis.parcels ?? [];
  const { data: caps } = useZECapabilities();
  const warnings = computeWarnings(sig, analysis.oddzial, caps);
  const reanalyze = useReanalyzeZETender(tenderId);
  const [reanalyzeTaskId, setReanalyzeTaskId] = useState<string | null>(null);
  const reanalyzeTask = useTaskPolling(reanalyzeTaskId);
  const isReanalyzing =
    reanalyzeTaskId !== null &&
    reanalyzeTask.data?.status &&
    !["success", "failure", "cancelled"].includes(reanalyzeTask.data.status);

  return (
    <div className="mt-4 max-w-3xl space-y-5">
      {warnings.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 space-y-1">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
            Ostrzeżenia dot. uprawnień
          </p>
          {warnings.map((w, i) => (
            <p
              key={i}
              className="text-xs text-amber-700 dark:text-amber-400 flex gap-1.5"
            >
              <span className="shrink-0">⚠</span>
              {w}
            </p>
          ))}
        </div>
      )}
      <div className="card p-4 space-y-1">
        <div className="flex items-center justify-between gap-2 pb-2 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-violet-500" />
            <span className="text-xs font-mono uppercase tracking-widest text-[var(--color-text-tertiary)]">
              Dane wygenerowane przez AI · tylko do weryfikacji
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            disabled={reanalyze.isPending || isReanalyzing}
            loading={reanalyze.isPending || isReanalyzing}
            onClick={() =>
              reanalyze.mutate(undefined, {
                onSuccess: (r) => setReanalyzeTaskId(r.task_id),
              })
            }
          >
            {isReanalyzing ? "Analizuję…" : "Przeanalizuj ponownie"}
          </Button>
        </div>
        <AiRow label="Nr RfX (ELOG/...)" value={analysis.rfx_number || null} />
        <AiRow label="ID RfX" value={analysis.rfx_id || null} />
        <AiRow label="Oddział ENERGA" value={analysis.oddzial || null} />
        <AiRow label="Rejon dystrybucji" value={sig?.energa_rejon || null} />
        <AiRow label="Typ sieci" value={sig?.network_type || null} />
        <AiRow
          label="Typ projektu"
          value={
            sig?.project_type
              ? (PROJECT_TYPE_LABEL[sig.project_type] ?? sig.project_type)
              : null
          }
        />
        <AiRow
          label="Zakres"
          value={
            sig?.contract_scope
              ? (SCOPE_LABEL[sig.contract_scope] ?? sig.contract_scope)
              : null
          }
        />
        <AiRow
          label="Dł. kabla"
          value={
            sig?.cable_length_m != null
              ? `${sig.cable_length_m.toLocaleString("pl-PL")} m`
              : null
          }
        />
        <AiRow
          label="Dł. demontażu"
          value={
            sig?.demolished_length_m != null
              ? `${sig.demolished_length_m.toLocaleString("pl-PL")} m`
              : null
          }
        />
        <AiRow label="Miejscowość" value={sig?.locality || null} />
        <AiRow label="Gmina" value={sig?.gmina || null} />
        <AiRow
          label="Pewność lokalizacji"
          value={
            analysis.confidence
              ? (CONFIDENCE_LABEL[analysis.confidence] ?? analysis.confidence)
              : null
          }
        />
        {(sig?.rkw_required?.length ?? 0) > 0 && (
          <AiRow
            label="RKW wymagane"
            value={
              <RkwBadges
                rkw={sig!.rkw_required}
                scope={sig!.contract_scope}
                warnings={warnings}
              />
            }
          />
        )}
        {sig?.reasoning && (
          <AiRow
            label="Uzasadnienie AI"
            value={
              <em className="text-[var(--color-text-secondary)]">
                {sig.reasoning}
              </em>
            }
          />
        )}
      </div>

      {parcels.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-2 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
            <span className="text-xs font-mono uppercase tracking-widest text-[var(--color-text-tertiary)]">
              Działki (AI) — {parcels.length}
            </span>
          </div>
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-[var(--color-border-muted)]">
                <th className="text-left px-4 py-2 text-[var(--color-text-tertiary)] font-normal">
                  Nr ewidencyjny
                </th>
                <th className="text-left px-4 py-2 text-[var(--color-text-tertiary)] font-normal">
                  Obręb
                </th>
                <th className="text-right px-4 py-2 text-[var(--color-text-tertiary)] font-normal">
                  Pewność
                </th>
              </tr>
            </thead>
            <tbody>
              {parcels.map((p) => (
                <tr
                  key={p.teryt}
                  className="border-b border-[var(--color-border-muted)] last:border-0"
                >
                  <td className="px-4 py-2 text-[var(--color-text-primary)]">
                    {p.teryt}
                  </td>
                  <td className="px-4 py-2 text-[var(--color-text-secondary)]">
                    {p.obreb || "—"}
                  </td>
                  <td
                    className={`px-4 py-2 text-right ${CONFIDENCE_COLOR[p.confidence] ?? "text-[var(--color-text-secondary)]"}`}
                  >
                    {CONFIDENCE_LABEL[p.confidence] ?? p.confidence} | (funkcja
                    w fazie rozwoju - AI może się mylić)
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ZETenderWorkspaceContent() {
  const { id = "" } = useParams<{ id: string }>();
  const { data: tender, isLoading } = useZETender(id);
  const { data: elementTypes } = useZEElementTypes();
  const { data: variants } = useZEVariants(id);

  // A mention notification links to ?tab=conversation(&message=<id>) — that thread lives
  // in the embedded tender page, so open "Przetarg" instead of the drawing.
  const [searchParams] = useSearchParams();
  const wantsConversation =
    searchParams.get("tab") === "conversation" || !!searchParams.get("message");

  const [tab, setTab] = useState<"draw" | "tender" | "kosztorys" | "ai">(
    wantsConversation ? "tender" : "draw",
  );
  // Clicking the bell while already on this workspace only swaps the query string, so
  // the initial state above never re-runs — react to the change here too.
  useEffect(() => {
    if (wantsConversation) setTab("tender");
  }, [wantsConversation]);
  const [fitTrigger, setFitTrigger] = useState(0);
  const drawingMapRef = useRef<ZEDrawingMapHandle>(null);
  const [highlightTeryt, setHighlightTeryt] = useState<string | null>(null);

  // ── Fullscreen resizable columns ─────────────────────────────────────────
  const [fsWidths, setFsWidths] = useState(loadFsWidths);
  const fsWidthsRef = useRef(fsWidths);
  fsWidthsRef.current = fsWidths;

  // The pricing panel needs more than preset_params: `lines`/`objects` to tell "nothing
  // drawn" from "everything counted zero", and `road_neighbors` to judge whether the
  // neighbour keys can be trusted.
  const loadZESummary = useCallback(
    (variantId: string) => zeApi.getQuantitySummary(variantId),
    [],
  );

  const applyFsWidths = useCallback((next: typeof FS_WIDTHS_DEFAULT) => {
    setFsWidths(next);
    localStorage.setItem(FS_WIDTHS_KEY, JSON.stringify(next));
  }, []);

  const startResize = useCallback(
    (side: "left" | "right") => (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = fsWidthsRef.current[side];
      const onMove = (ev: MouseEvent) => {
        const delta =
          side === "left" ? ev.clientX - startX : startX - ev.clientX;
        const min = side === "left" ? 220 : 160;
        const max = side === "left" ? 560 : 420;
        applyFsWidths({
          ...fsWidthsRef.current,
          [side]: Math.max(min, Math.min(max, startW + delta)),
        });
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [applyFsWidths],
  );
  const [activeVariantId, setActiveVariantId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<ZEElementType | null>(null);
  const [dirty, setDirty] = useState(false);
  const [currentFc, setCurrentFc] = useState<ZEDrawingFeatureCollection | null>(
    null,
  );
  const [taskId, setTaskId] = useState<string | null>(null);
  const [mpzpTaskId, setMpzpTaskId] = useState<string | null>(null);
  const [showMpzpGaps, setShowMpzpGaps] = useState(false);
  const [paTaskId, setPaTaskId] = useState<string | null>(null);
  const [showProtectedAreas, setShowProtectedAreas] = useState(false);
  const [waterTaskId, setWaterTaskId] = useState<string | null>(null);
  const [showWaterCrossings, setShowWaterCrossings] = useState(false);
  const [roadNeighborsTaskId, setRoadNeighborsTaskId] = useState<string | null>(null);

  // Fullscreen covers the WHOLE drawing workspace (variant switcher + map + element
  // palette + save) — not just the map — so everything needed to draw stays on screen.
  const fsRef = useRef<HTMLDivElement | null>(null);
  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else fsRef.current?.requestFullscreen?.();
  };

  const types = useMemo(() => elementTypes ?? [], [elementTypes]);
  const typesByCode = useMemo(() => {
    const m: Record<string, ZEElementType> = {};
    for (const t of types) m[t.code] = t;
    return m;
  }, [types]);

  // Default to the first variant once they load.
  useEffect(() => {
    if (!activeVariantId && variants && variants.length)
      setActiveVariantId(variants[0].id);
  }, [variants, activeVariantId]);

  const { data: drawing } = useVariantDrawing(activeVariantId ?? undefined);
  const { data: serverSummary } = useQuantitySummary(
    activeVariantId ?? undefined,
  );
  const { data: parcels } = useZETenderParcels(activeVariantId ?? undefined);
  const { data: roadNeighbors = [] } = useZERoadNeighbors(activeVariantId ?? undefined);
  const { data: drafts = [] } = useZETenderParcelDrafts(
    activeVariantId ?? undefined,
  );
  const saveDraft = useSaveZETenderParcelDraft(activeVariantId ?? "");
  const patchVariant = usePatchZEVariant(id ?? "");
  const qc = useQueryClient();
  const takeoff = useStartTakeoffJob();
  // Wycena PRZETARGU, nie czyjaś prywatna — ta sama dla każdego, kto ma dostęp do wycen.
  const { data: takeoffJob } = useTenderTakeoffJob(id);
  const [takeoffError, setTakeoffError] = useState<string | null>(null);
  const activeVariant = variants?.find((v) => v.id === activeVariantId);
  const mpzpGapGeom = activeVariant?.mpzp_gap_geom ?? null;
  const mpzpUnknownGeom = activeVariant?.mpzp_unknown_geom ?? null;
  const protectedAreasGeom = activeVariant?.protected_areas_geom ?? null;
  const waterCrossingsGeom = activeVariant?.water_crossings_geom ?? null;

  const { data: annotations = [] } = useZEMapAnnotations(activeVariantId ?? undefined);
  const createAnnotation = useCreateZEMapAnnotation(activeVariantId ?? "");
  const updateAnnotation = useUpdateZEMapAnnotation(activeVariantId ?? "");
  const deleteAnnotation = useDeleteZEMapAnnotation(activeVariantId ?? "");

  // Panel-initiated edit: switch map to annotate mode and open the editor for an existing pin.
  // We store the pending annotation externally so the workspace can trigger it on the map handle.
  const [pendingAnnotationEdit, setPendingAnnotationEdit] = useState<ZEMapAnnotation | null>(null);

  const save = useSaveDrawing(activeVariantId ?? "", id);
  const recompute = useRecomputeParcels(activeVariantId ?? "");
  const createVariant = useCreateZEVariant(id);
  const copyVariant = useCopyZEVariant(id);
  const deleteVariant = useDeleteZEVariant(id);
  const invalidateAfterRecompute = useInvalidateAfterRecompute();

  // Download the active variant's drawing as a DXF (route lines, objects, labels,
  // annotations and crossed parcels). Blob download so the JWT-authenticated client
  // fetches it — the browser uses `a.download` for the filename, not the server header.
  const [downloadingDxf, setDownloadingDxf] = useState(false);
  // Optional target layer preset (Tauron / Energa / …) to re-layer the DXF onto, so a
  // route copied from the tender lands on the layers a project expects. "" = tender layers.
  const [dxfPresetId, setDxfPresetId] = useState("");
  // Output CRS: 2180 (PUWG 1992, the drawing's native CRS) or a PUWG 2000 zone
  // 2176–2179, matching what the target CAD project expects.
  const [dxfSrid, setDxfSrid] = useState(2180);
  const { data: layerPresets } = useVariantLayerPresets(activeVariantId ?? undefined);
  const selectedPreset = layerPresets?.find((p) => p.id === dxfPresetId);
  const onDownloadDxf = useCallback(async () => {
    if (!activeVariantId) return;
    setDownloadingDxf(true);
    try {
      const blob = await zeApi.downloadVariantDxf(
        activeVariantId,
        dxfPresetId || undefined,
        dxfSrid,
      );
      const label = activeVariant?.label ?? "";
      const slug = (s: string) =>
        s.toLowerCase().replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "");
      const stem = slug(tender?.title ?? "przetarg-ze") || "przetarg-ze";
      let suffix = selectedPreset ? `_${slug(selectedPreset.name)}` : "";
      if (dxfSrid !== 2180) suffix += `_epsg${dxfSrid}`;
      downloadBlob(blob, `${stem}_wariant_${label}${suffix}.dxf`);
    } catch (err) {
      window.alert(extractError(err) || "Nie udało się pobrać pliku DXF.");
    } finally {
      setDownloadingDxf(false);
    }
  }, [activeVariantId, activeVariant?.label, tender?.title, dxfPresetId, dxfSrid, selectedPreset]);

  const { data: aiAnalysis } = useZETenderAiAnalysis(id);
  const { data: caps } = useZECapabilities();
  const task = useTaskPolling(taskId);
  const taskStatus = task.data?.status;
  useEffect(() => {
    if (taskStatus === "success" && activeVariantId)
      invalidateAfterRecompute(activeVariantId, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskStatus]);

  // MPZP task runs in parallel — invalidate variants when it finishes so mpzp_gap_geom appears.
  // Fall back to the variant's persisted task id so the spinner / "Przerwij" survive a reload
  // (session state is lost on refresh, which is why the cancel button used to never appear).
  const effectiveMpzpTaskId = mpzpTaskId ?? activeVariant?.mpzp_task_id ?? null;
  const mpzpTask = useTaskPolling(effectiveMpzpTaskId);
  const mpzpTaskStatus = mpzpTask.data?.status;
  useEffect(() => {
    if (mpzpTaskStatus === "success" && activeVariantId)
      invalidateAfterRecompute(activeVariantId, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpzpTaskStatus]);
  const cancelMpzp = useCancelTask(effectiveMpzpTaskId);

  // Protected-areas (GDOŚ) task — same parallel flow as MPZP, incl. the reload fallback.
  const effectivePaTaskId = paTaskId ?? activeVariant?.protected_areas_task_id ?? null;
  const paTask = useTaskPolling(effectivePaTaskId);
  const paTaskStatus = paTask.data?.status;
  useEffect(() => {
    if (paTaskStatus === "success" && activeVariantId)
      invalidateAfterRecompute(activeVariantId, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paTaskStatus]);
  const cancelPa = useCancelTask(effectivePaTaskId);

  // Przejścia przez wody (Hydroportal) — ten sam równoległy przebieg co MPZP/GDOŚ,
  // z odzyskaniem spinnera po przeładowaniu strony.
  const effectiveWaterTaskId =
    waterTaskId ?? activeVariant?.water_crossings_task_id ?? null;
  const waterTask = useTaskPolling(effectiveWaterTaskId);
  const waterTaskStatus = waterTask.data?.status;
  useEffect(() => {
    if (waterTaskStatus === "success" && activeVariantId)
      invalidateAfterRecompute(activeVariantId, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waterTaskStatus]);
  const cancelWater = useCancelTask(effectiveWaterTaskId);

  // Internal-road neighbours task (przelicz sąsiadów dróg) — refresh the neighbour list
  // and the summary keys once it lands.
  const recomputeRoadNeighbors = useRecomputeRoadNeighbors();
  const roadNeighborsTask = useTaskPolling(roadNeighborsTaskId);
  const roadNeighborsTaskStatus = roadNeighborsTask.data?.status;
  // The pricing panel can recompute a variant other than the active one, so invalidate the
  // variant the task actually ran for.
  const [roadNeighborsVariantId, setRoadNeighborsVariantId] = useState<string | null>(null);
  useEffect(() => {
    const target = roadNeighborsVariantId ?? activeVariantId;
    if (roadNeighborsTaskStatus === "success" && target)
      invalidateAfterRecompute(target, id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadNeighborsTaskStatus]);
  const recomputeRoadNeighborsFor = useCallback(
    (vid: string) =>
      new Promise<void>((resolve) => {
        recomputeRoadNeighbors.mutate(vid, {
          onSuccess: (r) => {
            setRoadNeighborsVariantId(vid);
            setRoadNeighborsTaskId(r.task_id);
            resolve();
          },
          onError: () => resolve(),
        });
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const doRecomputeRoadNeighbors = () => {
    if (activeVariantId) void recomputeRoadNeighborsFor(activeVariantId);
  };
  // Shared across both panels so they never disagree about whether a sweep is in flight.
  const roadNeighborsBusy =
    recomputeRoadNeighbors.isPending ||
    ["pending", "running"].includes(roadNeighborsTask.data?.status ?? "");

  // Surface a failed recompute/save request instead of silently swallowing it.
  const recomputeError =
    recompute.isError || save.isError
      ? extractError(recompute.error ?? save.error)
      : null;

  const selectVariant = (vid: string) => {
    setActiveVariantId(vid);
    setCurrentFc(null);
    setDirty(false);
    setTaskId(null);
    setMpzpTaskId(null);
    setPaTaskId(null);
    setWaterTaskId(null);
    setRoadNeighborsTaskId(null);
    setHighlightTeryt(null);
  };

  const localSummary = useMemo(
    () =>
      currentFc ? localQuantitySummary(currentFc, typesByCode) : EMPTY_SUMMARY,
    [currentFc, typesByCode],
  );
  const displaySummary = dirty ? localSummary : (serverSummary ?? localSummary);

  const hasLines = useMemo(
    () =>
      (currentFc?.features ?? []).some(
        (f) => typesByCode[f.properties?.element_type]?.kind === "line",
      ),
    [currentFc, typesByCode],
  );

  // Drawn point objects get their działka discovered too (backend _merge_object_parcels),
  // so a variant with only objects and no lines can still be recomputed.
  const hasObjects = useMemo(
    () =>
      (currentFc?.features ?? []).some(
        (f) => typesByCode[f.properties?.element_type]?.kind === "object",
      ),
    [currentFc, typesByCode],
  );

  const doSave = () => {
    if (!currentFc || !activeVariantId) return;
    save.mutate(currentFc, { onSuccess: () => setDirty(false) });
  };

  /** Rysunek → przedmiar → kosztorys, jednym kliknięciem.
   *
   *  Obmiar liczy się z ZAPISANEJ geometrii, więc niezapisany rysunek trzeba najpierw
   *  odłożyć do bazy. Wcześniej przycisk po prostu czekał wyłączony, aż użytkownik zrobi
   *  to sam — i gubił rysunek komuś, kto tego nie zauważył. Teraz zapis jest częścią tej
   *  samej akcji. */
  const doTakeoff = () => {
    if (!activeVariantId) return;
    setTakeoffError(null);
    const generate = () =>
      takeoff.mutate(activeVariantId, {
        onSuccess: () => {
          // Zostajemy w warsztacie — kosztorys otwiera się obok, w zakładce.
          qc.invalidateQueries({ queryKey: ["est", "tender-job", id ?? ""] });
          setTab("kosztorys");
        },
        onError: (err) => setTakeoffError(extractError(err)),
      });
    if (dirty && currentFc) {
      save.mutate(currentFc, {
        onSuccess: () => {
          setDirty(false);
          generate();
        },
        onError: (err) => setTakeoffError(extractError(err)),
      });
      return;
    }
    generate();
  };

  const doSaveAndRecompute = () => {
    if (!currentFc || !activeVariantId) return;
    save.mutate(currentFc, {
      onSuccess: () => {
        setDirty(false);
        recompute.mutate(undefined, {
          onSuccess: (r) => {
            setTaskId(r.task_id);
            setMpzpTaskId(r.mpzp_task_id ?? null);
            setPaTaskId(r.protected_areas_task_id ?? null);
            setWaterTaskId(r.water_crossings_task_id ?? null);
          },
        });
      },
    });
  };

  const doRecompute = () => {
    recompute.mutate(undefined, {
      onSuccess: (r) => {
        setTaskId(r.task_id);
        setMpzpTaskId(r.mpzp_task_id ?? null);
        setPaTaskId(r.protected_areas_task_id ?? null);
        setWaterTaskId(r.water_crossings_task_id ?? null);
      },
    });
  };

  const doResetAllDrafts = () => {
    for (const p of parcels ?? []) {
      const uldkOpt = findOwnershipOption(p.ownership_code);
      saveDraft.mutate({
        teryt: p.teryt,
        data: {
          ownership_override: "",
          wpw_count: uldkOpt?.wpwDefault ?? null,
        },
      });
    }
  };

  if (isLoading)
    return (
      <div className="py-10 flex justify-center">
        <Spinner />
      </div>
    );
  if (!tender)
    return <div className="p-6 section-label">Nie znaleziono przetargu.</div>;

  // Compute uprawnienia warnings from AI analysis
  const capWarnings = aiAnalysis
    ? computeWarnings(aiAnalysis.signals, aiAnalysis.oddzial, caps)
    : [];
  const analysisUnreadable =
    aiAnalysis &&
    (aiAnalysis.confidence === "failed" ||
      aiAnalysis.confidence === "needs_vision" ||
      (aiAnalysis.signals?.rkw_required?.length === 0 &&
        !aiAnalysis.signals?.contract_scope));

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
      <PageHeader
        title={tender.title}
        subtitle={`Przetarg ZE · ${tender.client_name}`}
        backTo="/ze-tenders"
        backLabel="Przetargi ZE"
        actions={
          <div className="flex items-start gap-3 flex-wrap justify-end">
            {aiAnalysis && analysisUnreadable && (
              <div
                onClick={() => setTab("ai")}
                className="cursor-pointer"
                title="Kliknij → Analiza AI"
              >
                <StatTile label="Uprawnienia" value="⚠ Błąd" />
              </div>
            )}
            {aiAnalysis && !analysisUnreadable && capWarnings.length > 0 && (
              <div
                onClick={() => setTab("ai")}
                className="cursor-pointer"
                title={capWarnings.join("\n")}
              >
                <StatTile
                  label="Uprawnienia"
                  value={`⚠ Brak · ${capWarnings.length}`}
                  tone="danger"
                />
              </div>
            )}
            {aiAnalysis && !analysisUnreadable && capWarnings.length === 0 && (
              <div
                onClick={() => setTab("ai")}
                className="cursor-pointer"
                title="Kliknij → Analiza AI"
              >
                <StatTile label="Uprawnienia RKW" value="✓ OK" tone="ok" />
              </div>
            )}
            <StatTile
              label="Termin oferty"
              value={fmtDateTime(tender.offer_deadline)}
            />
            <StatTile label="Warianty" value={variants?.length ?? 0} />
          </div>
        }
      />

      {/* „Ten przetarg już raz był i został anulowany" — nad zakładkami, bo decyduje o
          tym, czy w ogóle rysować od nowa, a nie o szczegółach rysunku. */}
      <ZERepeatMatchBanner
        tenderId={id}
        onImported={(result) => {
          if (result.variant_id) {
            setTab("draw");
            selectVariant(result.variant_id);
            setFitTrigger((n) => n + 1);
          } else if (result.proposal_id) {
            setTab("tender");
          }
        }}
      />

      <Tabs
        tabs={[
          { key: "draw", label: "Rysunek i działki" },
          { key: "tender", label: "Przetarg" },
          { key: "kosztorys" as const, label: "Kosztorys" },
          ...(aiAnalysis ? [{ key: "ai" as const, label: "Analiza AI" }] : []),
        ]}
        active={tab}
        onChange={(t) => {
          setTab(t);
          if (t === "draw") setFitTrigger((n) => n + 1);
        }}
      />

      {tab === "tender" && (
        <TenderDetailPage
          embedded
          zeVariants={variants?.map((v) => ({
            id: v.id,
            label: v.label,
            mpzpCoveragePct: v.mpzp_coverage_pct,
            mpzpUnknownPct: v.mpzp_unknown_pct,
            mpzpComputedAt: v.mpzp_computed_at,
            hasMpzpGap: !!v.mpzp_gap_geom,
            hasProtectedAreas: (v.protected_areas?.length ?? 0) > 0,
          }))}
          onLoadZESummary={loadZESummary}
          onRecomputeRoadNeighbors={recomputeRoadNeighborsFor}
          roadNeighborsBusy={roadNeighborsBusy}
        />
      )}

      {tab === "kosztorys" && (
        takeoffJob ? (
          <EstimateResultPage jobId={takeoffJob.id} />
        ) : (
          <SectionCard title="Kosztorys z rysunku">
            <p className="text-sm text-[var(--color-text-secondary)]">
              Nie ma jeszcze wyceny tego przetargu. Narysuj trasę w zakładce „Rysunek
              i działki", zapisz i kliknij <strong>Wyceń z rysunku</strong> — kosztorys
              pojawi się tutaj i będzie widoczny dla wszystkich, którzy mają dostęp do wycen.
            </p>
          </SectionCard>
        )
      )}

      {tab === "ai" && aiAnalysis && (
        <AiAnalysisTab analysis={aiAnalysis} tenderId={id} />
      )}

      <div className={tab === "draw" ? "" : "hidden"}>
        {/* Fullscreen wraps the entire drawing workspace, so going fullscreen keeps the
          variant switcher, palette and save controls — not only the map. */}
        <div
          ref={fsRef}
          className={
            isFs
              ? "relative bg-[var(--color-bg-base)] h-screen flex flex-col gap-3 p-4 overflow-hidden"
              : ""
          }
        >
          <div
            className={isFs ? "shrink-0 flex items-center gap-2" : "mb-4"}
          >
            <ZEVariantSwitcher
              variants={variants ?? []}
              activeVariantId={activeVariantId}
              dirty={dirty}
              isFullscreen={isFs}
              onSelect={selectVariant}
              onCreate={(label) =>
                createVariant.mutate(
                  { label },
                  { onSuccess: (v) => selectVariant(v.id) },
                )
              }
              onCopyFrom={(sourceId, label) =>
                copyVariant.mutate(
                  { sourceId, label },
                  {
                    onSuccess: (v) => {
                      selectVariant(v.id);
                      // selectVariant() clears taskId/mpzpTaskId/paTaskId; re-arm them
                      // with the copy's recompute tasks so the progress spinner shows
                      // and the parcel counts auto-refresh once the recompute finishes.
                      if (v.recomputeTaskId) setTaskId(v.recomputeTaskId);
                      if (v.mpzpTaskId) setMpzpTaskId(v.mpzpTaskId);
                      if (v.protectedAreasTaskId) setPaTaskId(v.protectedAreasTaskId);
                      if (v.waterCrossingsTaskId) setWaterTaskId(v.waterCrossingsTaskId);
                    },
                  },
                )
              }
              copyPending={copyVariant.isPending}
              onDelete={(vid) =>
                deleteVariant.mutate(vid, {
                  onSuccess: () => {
                    const rest = (variants ?? []).filter((v) => v.id !== vid);
                    selectVariant(rest[0]?.id ?? "");
                    if (!rest.length) setActiveVariantId(null);
                  },
                })
              }
            />
            {isFs && (
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  title="Zamknij pełny ekran"
                  className="flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] border border-[var(--color-primary)] shadow-md px-3 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
                >
                  <Minimize2 size={18} />
                  <span>Zamknij</span>
                </button>
              </div>
            )}
          </div>

          {!activeVariantId ? (
            <SectionCard>
              <p className="text-sm text-[var(--color-text-secondary)]">
                Dodaj wariant, aby zacząć rysować trasę i obiekty.
              </p>
            </SectionCard>
          ) : (
            /* ─────────────────────────────────────────────────────────────────────
               ONE map, two layouts. The map (and palette / parcels / quantities) keep a
               FIXED position in the React tree across the fullscreen toggle — only the
               wrapper classes change — so Leaflet is never unmounted. That is what keeps
               the zoom/center, the GUGiK layer selection and any unsaved drawing alive
               when entering or leaving fullscreen (previously two separate <ZEDrawingMap>
               instances were swapped, which recreated the map and wiped all of it). CSS
               does the rearranging:
                 • normal     → flex-wrap: row 1 = [map | palette], row 2 = [quantities | parcels]
                 • fullscreen → flex row:  [parcels | ⠿ | map | ⠿ | palette] (resizable)
               ───────────────────────────────────────────────────────────────────── */
            <div
              className={
                isFs
                  ? "flex-1 min-h-0 flex overflow-hidden"
                  : "flex flex-wrap gap-5 items-start"
              }
            >
              {/* ── A: map + (right handle) + palette — always tree-index 0 ── */}
              <div
                className={
                  isFs
                    ? "order-2 flex min-h-0 min-w-0 flex-1"
                    : "basis-full grid lg:grid-cols-[1fr_300px] gap-5 items-start"
                }
              >
                {/* map + save controls */}
                <div
                  className={
                    isFs
                      ? "flex flex-col gap-2 min-h-0 flex-1 min-w-0"
                      : "space-y-3"
                  }
                >
                  <ErrorBoundary
                    label="ZEDrawingMap"
                    title="Nie udało się wyrenderować mapy"
                    hint={
                      "Dane przetargu i pozostałe zakładki działają normalnie. Jeśli ten sam " +
                      "przetarg otwiera się poprawnie na innej przeglądarce, zacznij od " +
                      "„Zresetuj ustawienia widoku” — warstwy mapy zapisują się osobno w każdej przeglądarce."
                    }
                    resetKeys={ZE_MAP_VIEW_KEYS}
                  >
                  <ZEDrawingMap
                    ref={drawingMapRef}
                    key={activeVariantId}
                    elementTypes={types}
                    activeType={activeType}
                    initialDrawing={drawing}
                    parcels={parcels ?? []}
                    isFullscreen={isFs}
                    onToggleFullscreen={toggleFullscreen}
                    fitTrigger={fitTrigger}
                    drafts={drafts}
                    onParcelClick={(teryt) => setHighlightTeryt(teryt)}
                    onChange={(fc, isDirty) => {
                      setCurrentFc(fc);
                      setDirty(isDirty);
                    }}
                    mpzpGapGeom={showMpzpGaps ? mpzpGapGeom : null}
                    mpzpUnknownGeom={showMpzpGaps ? mpzpUnknownGeom : null}
                    protectedAreasGeom={showProtectedAreas ? protectedAreasGeom : null}
                    waterCrossingsGeom={showWaterCrossings ? waterCrossingsGeom : null}
                    annotations={annotations}
                    onAnnotationCreate={async (lat, lng, text) =>
                      createAnnotation.mutateAsync({
                        variant: activeVariantId!,
                        text,
                        geometry: { type: "Point", coordinates: [lng, lat] },
                      })
                    }
                    onAnnotationUpdate={(annId, payload) =>
                      updateAnnotation.mutate({ id: annId, payload })
                    }
                    onAnnotationDelete={(annId) => deleteAnnotation.mutate(annId)}
                    externalAnnotationEdit={pendingAnnotationEdit}
                    onExternalAnnotationEditHandled={() =>
                      setPendingAnnotationEdit(null)
                    }
                    takeoffDefaults={activeVariant?.takeoff_defaults}
                  />
                  </ErrorBoundary>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <Button
                      disabled={!dirty}
                      loading={save.isPending || recompute.isPending}
                      onClick={doSaveAndRecompute}
                    >
                      Zapisz rysunek i przelicz ponownie działki
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={!dirty}
                      loading={save.isPending && !recompute.isPending}
                      onClick={doSave}
                    >
                      Zapisz bez przeliczenia działek
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={takeoff.isPending || save.isPending || !activeVariantId}
                      loading={takeoff.isPending || (save.isPending && dirty)}
                      title="Zapisz rysunek i policz z niego przedmiar — kosztorys otworzy się w zakładce obok"
                      onClick={doTakeoff}
                    >
                      {dirty ? "Zapisz i wyceń z rysunku" : "Wyceń z rysunku"}
                    </Button>
                    {takeoffError && (
                      <span className="font-mono text-[11px] text-[var(--color-primary)]">
                        {takeoffError}
                      </span>
                    )}
                    <div className="flex items-center gap-1.5">
                      {layerPresets && layerPresets.length > 0 ? (
                        <select
                          value={dxfPresetId}
                          onChange={(e) => setDxfPresetId(e.target.value)}
                          title="Warstwy DXF pobieranego pliku (dopasowanie po typach obiektów)"
                          className="h-8 rounded border border-gray-300 bg-white px-2 text-[13px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Warstwy przetargu (bez zmian)</option>
                          {layerPresets.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                              {p.unmapped.length > 0 ? ` (⚠ ${p.unmapped.length})` : ""}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      <select
                        value={dxfSrid}
                        onChange={(e) => setDxfSrid(Number(e.target.value))}
                        title="Układ współrzędnych pobieranego pliku DXF"
                        className="h-8 rounded border border-gray-300 bg-white px-2 text-[13px] text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value={2180}>EPSG:2180 — PUWG 1992</option>
                        <option value={2176}>EPSG:2176 — PUWG 2000 strefa 5 (15°E)</option>
                        <option value={2177}>EPSG:2177 — PUWG 2000 strefa 6 (18°E)</option>
                        <option value={2178}>EPSG:2178 — PUWG 2000 strefa 7 (21°E)</option>
                        <option value={2179}>EPSG:2179 — PUWG 2000 strefa 8 (24°E)</option>
                      </select>
                      <Button
                        variant="secondary"
                        loading={downloadingDxf}
                        onClick={onDownloadDxf}
                        title="Pobierz rysunek wariantu jako plik DXF (CAD)"
                      >
                        <Download size={16} />
                        Pobierz DXF
                      </Button>
                    </div>
                    {selectedPreset && selectedPreset.unmapped.length > 0 ? (
                      <span
                        className="text-[11px] text-amber-600"
                        title={`Typy bez warstwy w presecie „${selectedPreset.name}” zostaną na warstwach przetargu:\n${selectedPreset.unmapped
                          .map((u) => `• ${u.label}`)
                          .join("\n")}`}
                      >
                        {selectedPreset.unmapped.length} typ(ów) bez warstwy w presecie →
                        zostaną na warstwach przetargu
                      </span>
                    ) : null}
                    {dirty ? (
                      <span className="text-[11px] font-mono text-amber-600">
                        Niezapisane zmiany
                      </span>
                    ) : (
                      <span className="text-[11px] font-mono text-emerald-600">
                        Zapisano
                      </span>
                    )}
                  </div>
                </div>

                {/* DRAG HANDLE — right (between map and palette; fullscreen only) */}
                <div
                  onMouseDown={startResize("right")}
                  className={
                    isFs
                      ? "shrink-0 cursor-col-resize flex items-stretch px-3 group"
                      : "hidden"
                  }
                  title="Przeciągnij, aby zmienić szerokość"
                >
                  <div className="w-[3px] bg-[var(--color-primary)] opacity-40 group-hover:opacity-100 transition-opacity" />
                </div>

                {/* element palette — right of the map in BOTH layouts */}
                <div
                  className={isFs ? "shrink-0 min-h-0 overflow-y-auto" : ""}
                  style={
                    isFs
                      ? { width: fsWidths.right, minWidth: 160, maxWidth: 420 }
                      : undefined
                  }
                >
                  <ZEElementPalette
                    elementTypes={types}
                    activeTypeId={activeType?.id ?? null}
                    onSelect={(t) => setActiveType(t)}
                    isFullscreen={isFs}
                  />
                  {activeVariant && (
                    <ZETakeoffDefaultsPanel
                      elementTypes={types}
                      value={activeVariant.takeoff_defaults ?? {}}
                      saving={patchVariant.isPending}
                      isFullscreen={isFs}
                      onSave={(takeoff_defaults) =>
                        patchVariant.mutate({
                          id: activeVariant.id,
                          payload: { takeoff_defaults },
                        })
                      }
                    />
                  )}
                </div>
              </div>

              {/* ── C: quantity summary — bottom-left in normal, hidden in fullscreen ── */}
              <div
                className={
                  isFs ? "hidden" : "min-w-0 basis-full lg:basis-0 lg:grow-[2]"
                }
              >
                <ZEQuantitySummaryPanel
                  summary={displaySummary}
                  dirty={dirty}
                  elementTypes={types}
                />
              </div>

              {/* ── B: parcels — left column in fullscreen, bottom-right in normal ── */}
              <div
                className={
                  isFs
                    ? "order-none shrink-0 min-h-0 overflow-y-auto"
                    : "min-w-0 basis-full lg:basis-0 lg:grow-[3]"
                }
                style={
                  isFs
                    ? { width: fsWidths.left, minWidth: 220, maxWidth: 560 }
                    : undefined
                }
              >
                <ZEParcelsPanel
                  parcels={parcels ?? []}
                  drafts={drafts}
                  task={task.data}
                  isRecomputing={recompute.isPending}
                  canRecompute={(hasLines || hasObjects) && !dirty}
                  dirty={dirty}
                  hasLines={hasLines}
                  hasObjects={hasObjects}
                  onRecompute={doRecompute}
                  recomputeError={recomputeError}
                  mpzpTask={mpzpTask.data}
                  onCancelMpzp={() => cancelMpzp.mutate()}
                  isCancellingMpzp={cancelMpzp.isPending}
                  highlightTeryt={highlightTeryt}
                  onFitToParcel={(teryt) => {
                    setTab("draw");
                    drawingMapRef.current?.fitToParcel(teryt);
                  }}
                  onSaveDraft={(teryt, data) =>
                    saveDraft.mutate({ teryt, data })
                  }
                  onResetAllDrafts={doResetAllDrafts}
                  isResettingDrafts={saveDraft.isPending}
                  mpzpCoveragePct={activeVariant?.mpzp_coverage_pct}
                  mpzpUnknownPct={activeVariant?.mpzp_unknown_pct}
                  mpzpComputedAt={activeVariant?.mpzp_computed_at}
                  hasMpzpGap={!!mpzpGapGeom}
                  hasMpzpOverlay={!!mpzpGapGeom || !!mpzpUnknownGeom}
                  showMpzpGaps={showMpzpGaps}
                  onToggleMpzpGaps={setShowMpzpGaps}
                  protectedAreas={activeVariant?.protected_areas ?? null}
                  protectedAreasCoveragePct={activeVariant?.protected_areas_coverage_pct}
                  protectedAreasComputedAt={activeVariant?.protected_areas_computed_at}
                  protectedAreasTask={paTask.data}
                  onCancelProtectedAreas={() => cancelPa.mutate()}
                  isCancellingProtectedAreas={cancelPa.isPending}
                  showProtectedAreas={showProtectedAreas}
                  onToggleProtectedAreas={setShowProtectedAreas}
                  hasProtectedAreasOverlay={!!protectedAreasGeom}
                  waterCrossings={activeVariant?.water_crossings ?? null}
                  waterCrossingsComputedAt={activeVariant?.water_crossings_computed_at}
                  waterCrossingsTask={waterTask.data}
                  onCancelWaterCrossings={() => cancelWater.mutate()}
                  isCancellingWaterCrossings={cancelWater.isPending}
                  showWaterCrossings={showWaterCrossings}
                  onToggleWaterCrossings={setShowWaterCrossings}
                  hasWaterCrossingsOverlay={!!waterCrossingsGeom}
                  roadNeighbors={roadNeighbors}
                  roadNeighborsInfo={serverSummary?.road_neighbors}
                  roadNeighborsTask={roadNeighborsTask.data}
                  onRecomputeRoadNeighbors={doRecomputeRoadNeighbors}
                  isRecomputingRoadNeighbors={roadNeighborsBusy}
                />
              </div>

              {/* DRAG HANDLE — left (between parcels and map; fullscreen only) */}
              <div
                onMouseDown={startResize("left")}
                className={
                  isFs
                    ? "order-1 shrink-0 cursor-col-resize flex items-stretch px-3 group"
                    : "hidden"
                }
                title="Przeciągnij, aby zmienić szerokość"
              >
                <div className="w-[3px] bg-[var(--color-primary)] opacity-40 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          )}


        </div>
      </div>
    </div>
  );
}

/**
 * The route element. The boundary has to sit OUTSIDE the workspace component: React only
 * catches errors thrown BELOW a boundary, and the crash reports we are chasing come from
 * the drawing subtree's effects. Without this, that throw reaches the router's
 * `errorElement` and swaps the entire application for the generic "Błąd" page — the user
 * loses the tender, the navigation and the error message in one go.
 */
export function ZETenderWorkspacePage() {
  return (
    <ErrorBoundary
      label="ZETenderWorkspacePage"
      title="Nie udało się otworzyć przetargu"
      hint="Skopiuj szczegóły techniczne do zgłoszenia. Jeśli ten sam przetarg otwiera się na innej przeglądarce, spróbuj najpierw zresetować ustawienia widoku — zapisują się osobno w każdej przeglądarce."
      resetKeys={ZE_MAP_VIEW_KEYS}
    >
      <ZETenderWorkspaceContent />
    </ErrorBoundary>
  );
}
